/**
 * Library — the same catalogue the studio's picker uses, at full density, plus
 * the affordances for adding your own items.
 */
import { useRef, useState } from 'react';
import { api, type Category, type LibraryItem, type Swatch } from '../lib/api';
import Catalog from '../components/Catalog';
import { ROLES, roleForCategory } from '../lib/compose';

export interface LibraryProps {
  items: LibraryItem[];
  onChanged: () => void;
  onUse: (item: LibraryItem, swatch: Swatch) => void;
}

const CATEGORIES: Category[] = ['outfits', 'places', 'objects', 'looks'];

export default function Library({ items, onChanged, onUse }: LibraryProps) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const pendingItem = useRef<string | null>(null);

  async function guard(fn: () => Promise<void>) {
    setError(null); setBusy(true);
    try { await fn(); onChanged(); }
    catch (err) { setError(err instanceof Error ? err.message : 'That did not work'); }
    finally { setBusy(false); }
  }

  function newItem() {
    void guard(async () => {
      const name = window.prompt('What is it? (e.g. "Belted wool coat")')?.trim();
      if (!name) return;
      const category = (window.prompt(`Which shelf? ${CATEGORIES.join(', ')}`, 'outfits')?.trim() ?? '') as Category;
      if (!CATEGORIES.includes(category)) throw new Error(`Category must be one of: ${CATEGORIES.join(', ')}`);
      const subcategory = window.prompt('Sub-shelf? (optional, e.g. "Coats")')?.trim() ?? '';
      const res = await api.post<{ item: LibraryItem }>('/library', { name, category, subcategory });
      pendingItem.current = res.item.id;
      fileRef.current?.click();
    });
  }

  return (
    <div className="pad">
      <div className="page-head">
        <div>
          <h2>Library</h2>
          <p>
            Browse by category, pick a colour, send it to the studio. Some of it ships with FluidDream;
            the rest is whatever you've uploaded.
          </p>
        </div>
        <div className="spacer">
          <button className="btn btn-primary" disabled={busy} onClick={newItem}>Add item</button>
        </div>
      </div>

      {error && <div className="notice bad" style={{ marginBottom: 16 }}>{error}</div>}

      <Catalog
        items={items}
        actionLabel="Send to studio"
        onUse={onUse}
        hint={(item) => `Goes in as “${ROLES.find((r) => r.id === roleForCategory(item.category))?.label}”.`}
        onDelete={(item) => void guard(async () => {
          if (!window.confirm(`Delete ${item.name}?`)) return;
          await api.del(`/library/${item.id}`);
        })}
      />

      <p className="faint" style={{ fontSize: 11.5, marginTop: 14, maxWidth: '70ch', lineHeight: 1.6 }}>
        An item can hold several colourways — add more than one image to the same item and they show up
        as swatches rather than as separate entries.
      </p>

      <input
        ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" hidden
        onChange={(e) => {
          const f = e.target.files?.[0];
          const itemId = pendingItem.current;
          e.target.value = '';
          pendingItem.current = null;
          if (!f || !itemId) return;
          void guard(async () => {
            const form = new FormData();
            form.append('file', f);
            form.append('name', window.prompt('Name this colour? (optional)')?.trim() ?? '');
            await api.upload<{ swatch: Swatch }>(`/library/${itemId}/swatches`, form);
          });
        }}
      />
    </div>
  );
}
